/* ==============================================================================
   Governador de banda
   ==============================================================================
   O componente de maior risco do projeto, e a razão é aritmética:

     2 telas × 7 pares = 14 fluxos de vídeo de upload SIMULTÂNEOS

   Uma conexão doméstica típica no Brasil tem entre 5 e 40 Mbps de upload. Quatorze
   fluxos de tela a 2,5 Mbps cada pedem 35 Mbps. Sem controle explícito, o que
   acontece não é "fica um pouco pior": a fila de saída enche, a latência de TODOS
   os fluxos sobe junto, o áudio começa a cortar, e o usuário não tem como saber
   por quê — ele só vê que "o Concord travou".

   A abordagem, e por que ela é assim
   ==================================

   1. **Orçamento explícito, medido e configurável.**
      O ponto de partida é `availableOutgoingBitrate` do próprio WebRTC, que é a
      estimativa do algoritmo de congestionamento. Ela é boa e é reativa: sobe
      devagar e desce rápido. O usuário pode fixar um teto menor — e vai querer,
      se divide a rede com outras pessoas.

   2. **Prioridade estrita, não proporcional.**
      Áudio é INTOCÁVEL. Não é "prioridade alta": ele é subtraído do orçamento
      antes de qualquer divisão. Voz cortada torna a chamada inútil; tela em 15
      fps ainda é uma chamada. Dividir proporcionalmente degradaria o áudio para
      manter a tela — a decisão errada.

   3. **Histerese.**
      A estimativa de banda oscila naturalmente. Reagir a cada amostra produziria
      resolução mudando a cada dois segundos, que é visualmente pior do que
      qualidade estável mais baixa. O governador só age quando o desvio persiste.

   4. **Degradação em ordem declarada, e a UI diz o que aconteceu.**
      camadas → fps → resolução → desliga a tela secundária. E quando desliga,
      informa COM OS NÚMEROS: "2 telas × 7 pares excede seu upload de 10 Mbps —
      tela 2 pausada". Limite silencioso é o que faz o usuário culpar o produto.

   Ver docs/ARCHITECTURE.md §9.
   ============================================================================== */

import type { LocalTrack, PeerState, TrackPurpose } from './mesh.ts';

/* ------------------------------------------------------------------------------
   Custos de referência
   ------------------------------------------------------------------------------
   Em kbps POR PAR. São pontos de partida para a alocação, não limites rígidos —
   o codificador fica abaixo quando a cena é estática.

   Os valores de tela são maiores que os de câmera pelo motivo oposto ao
   intuitivo: câmera tem movimento contínuo e o codificador aproveita a predição
   temporal; tela é estática e depois muda TUDO de uma vez (rolagem, troca de
   janela), o que produz picos de quadro-chave grandes.
------------------------------------------------------------------------------ */
const AUDIO_KBPS = 40;

/** Perfis de vídeo, do melhor para o pior. A degradação percorre esta lista. */
export interface VideoProfile {
  name: string;
  maxKbps: number;
  maxFramerate: number;
  /** Divisor da resolução. 1 = original, 2 = metade em cada eixo. */
  scaleDownBy: number;
}

const SCREEN_PROFILES: VideoProfile[] = [
  { name: 'nítido', maxKbps: 2500, maxFramerate: 30, scaleDownBy: 1 },
  { name: 'bom', maxKbps: 1200, maxFramerate: 20, scaleDownBy: 1 },
  { name: 'leitura', maxKbps: 700, maxFramerate: 10, scaleDownBy: 1 },
  /*
   * Só o ÚLTIMO perfil reduz a resolução, e a ordem é deliberada.
   *
   * Em compartilhamento de tela, reduzir resolução destrói a legibilidade do
   * texto — que é o conteúdo principal. Reduzir FPS de 30 para 10 é quase
   * imperceptível num documento e economiza dois terços da banda. Para câmera a
   * ordem seria oposta, e é por isso que os perfis são separados.
   */
  { name: 'mínimo', maxKbps: 350, maxFramerate: 8, scaleDownBy: 2 },
];

const CAMERA_PROFILES: VideoProfile[] = [
  { name: 'alta', maxKbps: 1200, maxFramerate: 30, scaleDownBy: 1 },
  { name: 'média', maxKbps: 600, maxFramerate: 24, scaleDownBy: 2 },
  { name: 'baixa', maxKbps: 250, maxFramerate: 20, scaleDownBy: 4 },
];

export interface BandwidthBudget {
  /** Teto configurado pelo usuário, em kbps. `null` = usar a estimativa. */
  userCeilingKbps: number | null;
  /** Estimativa do WebRTC, em kbps. `null` antes da primeira medição. */
  estimatedKbps: number | null;
  /** Piso de segurança: nunca considera o orçamento menor que isto. */
  floorKbps: number;
}

export interface AllocationDecision {
  /** Orçamento efetivo usado na decisão. */
  budgetKbps: number;
  /** Upload projetado com a alocação atual. */
  projectedKbps: number;
  /** Reservado para áudio, subtraído antes de qualquer divisão. */
  audioKbps: number;
  /** Perfil escolhido por fonte de vídeo. */
  profiles: Map<string, VideoProfile>;
  /** Fontes que o governador PAUSOU por falta de orçamento. */
  paused: string[];
  /** Explicação para a interface, com números. Vazia quando não há aperto. */
  notice: string | null;
  /** `true` se o orçamento não cabe nem no perfil mínimo. */
  critical: boolean;
}

export interface GovernorOptions {
  budget: BandwidthBudget;
  /** Chamado quando uma fonte é pausada ou retomada. */
  onSourcePaused?: (sourceId: string, paused: boolean, reason: string) => void;
  onDecision?: (decision: AllocationDecision) => void;
}

/**
 * Quantas amostras consecutivas precisam concordar antes de agir.
 *
 * Com o laço de 2 segundos, 2 amostras = 4 segundos de aperto sustentado. É a
 * histerese: sem ela, um pico de 2 segundos ao abrir uma aba pesada rebaixaria a
 * qualidade da chamada inteira, e a recuperação levaria outro tanto.
 */
const HYSTERESIS_SAMPLES = 2;

/**
 * Amostras de vídeo fluindo antes de a estimativa valer como teto.
 *
 * Com o laço de 2 segundos, 3 amostras são ~6 segundos. É o tempo que o controle
 * de congestionamento do WebRTC leva para sondar o link para cima depois de o
 * vídeo começar — ele sobe devagar de propósito, para não causar o
 * congestionamento que está tentando medir.
 *
 * Confiar na estimativa antes disso produziria o oposto do defeito que este
 * mecanismo corrige: a tela começaria e seria imediatamente reduzida a 15 fps,
 * porque a medição do primeiro segundo ainda reflete o estado anterior.
 */
const BWE_RAMP_SAMPLES = 3;

export class BandwidthGovernor {
  private budget: BandwidthBudget;
  private readonly options: GovernorOptions;

  /** Perfil em vigor por sourceId. */
  private readonly current = new Map<string, VideoProfile>();
  private readonly pausedSources = new Set<string>();

  /** Contadores de histerese. */
  /*
   * Quantas amostras seguidas houve VÍDEO saindo.
   *
   * O que este contador resolve, e era um travamento completo
   * ========================================================
   * `availableOutgoingBitrate` é a estimativa do controle de congestionamento, e
   * ela só CRESCE enquanto se envia — o algoritmo descobre o teto sondando. Numa
   * chamada de áudio (~40 kbps por par) não há o que sondar, então a estimativa
   * fica perto do que está sendo enviado.
   *
   * Medido em produção com 3 pares: estimativa de ~1,3 Mbps, orçamento de 1,1
   * Mbps depois da margem de 15% — e uma tela para 3 pares pede 1,2 Mbps. O
   * governador recusava:
   *
   *     "Não cabe: 1 telas para 3 pares pedem 1.2 Mbps e o orçamento é 1.1 Mbps"
   *
   * E o laço se fecha: não se consegue estimativa alta sem enviar mais, e não se
   * envia mais porque a estimativa está baixa. Compartilhar tela ficava
   * PERMANENTEMENTE impossível numa chamada de áudio, em qualquer conexão.
   *
   * O erro conceitual era tratar a estimativa como um teto sempre válido. Ela é
   * uma MEDIÇÃO do que o link entregou — e sem vídeo não houve o que medir.
   */
  private videoActiveSamples = 0;

  private overBudgetStreak = 0;
  private underBudgetStreak = 0;

  private timer: ReturnType<typeof setInterval> | undefined;

  constructor(options: GovernorOptions) {
    this.options = options;
    this.budget = options.budget;
  }

  setUserCeiling(kbps: number | null): void {
    this.budget = { ...this.budget, userCeilingKbps: kbps };
    // Mudança de teto pelo usuário é uma intenção EXPLÍCITA: age na hora, sem
    // esperar a histerese. Esperar aqui pareceria que o controle não funciona.
    this.overBudgetStreak = HYSTERESIS_SAMPLES;
    this.underBudgetStreak = HYSTERESIS_SAMPLES;
  }

  /**
   * Orçamento efetivo: o menor entre teto do usuário e estimativa CONFIÁVEL.
   *
   * "Confiável" é a palavra que faltava. Ver `videoActiveSamples` para o defeito
   * que a ausência dela causava — em resumo: uma estimativa colhida sem vídeo
   * fluindo mede o áudio, não o link, e usá-la como teto impedia o vídeo de
   * jamais começar.
   */
  effectiveBudgetKbps(): number {
    const candidates: number[] = [];
    if (this.budget.userCeilingKbps !== null) candidates.push(this.budget.userCeilingKbps);

    const estimativaConfiavel = this.videoActiveSamples >= BWE_RAMP_SAMPLES;

    if (this.budget.estimatedKbps !== null && estimativaConfiavel) {
      /*
       * A estimativa entra com margem de 85%.
       *
       * `availableOutgoingBitrate` é o que o algoritmo de congestionamento acha
       * que cabe — e ele descobre o teto ENCHENDO a fila. Mirar 100% dela
       * significa operar permanentemente no ponto em que a latência começa a
       * subir. Os 15% de folga são a diferença entre "usa toda a banda" e "a
       * chamada fica estável".
       */
      candidates.push(Math.round(this.budget.estimatedKbps * 0.85));
    }
    if (candidates.length === 0) {
      // Sem medição nem teto: assume 12 Mbps, que é uma conexão doméstica
      // razoável. A primeira medição corrige em 2 segundos.
      return 12_000;
    }
    return Math.max(this.budget.floorKbps, Math.min(...candidates));
  }

  /**
   * Decide a alocação sem aplicar.
   *
   * Separado de `apply` de propósito: a interface precisa poder PREVER o efeito
   * de ligar uma segunda tela antes de ligá-la — que é o aviso "acima de 4 pares,
   * avise antes da segunda tela" da arquitetura. Uma função que decide e aplica
   * junto não serviria para isso.
   */
  decide(input: {
    peerCount: number;
    localTracks: LocalTrack[];
  }): AllocationDecision {
    const budgetKbps = this.effectiveBudgetKbps();
    const peers = Math.max(1, input.peerCount);

    const audioSources = input.localTracks.filter((t) => t.purpose === 'microphone');
    const videoSources = input.localTracks.filter((t) => t.purpose !== 'microphone');

    /*
     * Áudio primeiro, e fora da divisão.
     *
     * `audioKbps` é subtraído do orçamento antes de o vídeo ser considerado. É a
     * implementação de "áudio é intocável" — e é por isso que uma chamada com
     * banda péssima ainda tem voz limpa, com a tela pausada.
     */
    const audioKbps = audioSources.length * AUDIO_KBPS * peers;
    const videoBudget = Math.max(0, budgetKbps - audioKbps);

    const profiles = new Map<string, VideoProfile>();
    const paused: string[] = [];

    if (videoSources.length === 0) {
      const decision: AllocationDecision = {
        budgetKbps,
        projectedKbps: audioKbps,
        audioKbps,
        profiles,
        paused,
        notice: null,
        critical: audioKbps > budgetKbps,
      };
      return decision;
    }

    /*
     * Ordena as fontes por prioridade: tela ATIVA > tela secundária > câmera.
     *
     * "Ativa" é a primeira tela compartilhada — a que a pessoa escolheu primeiro
     * e provavelmente é o assunto da conversa. A câmera vem por último de
     * propósito: numa chamada de trabalho, ver o rosto importa menos que ler o
     * código que está sendo mostrado.
     */
    const ordered = [...videoSources].sort((a, b) => {
      const rank = (purpose: TrackPurpose, index: number): number => {
        if (purpose === 'screen') return index === 0 ? 0 : 1;
        return 2;
      };
      const aIndex = videoSources.filter((v) => v.purpose === 'screen').indexOf(a);
      const bIndex = videoSources.filter((v) => v.purpose === 'screen').indexOf(b);
      return rank(a.purpose, aIndex) - rank(b.purpose, bIndex);
    });

    let remaining = videoBudget;

    for (const source of ordered) {
      const table = source.purpose === 'screen' ? SCREEN_PROFILES : CAMERA_PROFILES;

      /*
       * Escolhe o melhor perfil que CABE, considerando o custo × pares.
       *
       * O `× peers` é o ponto inteiro deste arquivo. Um perfil de 2500 kbps não
       * custa 2500: custa 2500 × 7 = 17.500 kbps numa sala de 8. Esquecer essa
       * multiplicação é o erro que faz o compartilhamento de tela "funcionar em
       * teste com duas pessoas e falhar em reunião".
       */
      const affordable = table.find((profile) => profile.maxKbps * peers <= remaining);

      if (affordable) {
        profiles.set(source.sourceId, affordable);
        remaining -= affordable.maxKbps * peers;
        continue;
      }

      /*
       * Não cabe nem no perfil mínimo: PAUSA a fonte.
       *
       * Pausar é melhor que enviar um fluxo que não cabe. Um vídeo que não cabe
       * não fica "pior": ele enche a fila, e a fila cheia arrasta o áudio junto.
       * A primeira tela nunca é pausada — se nem ela cabe, o caso é `critical` e
       * a interface precisa dizer que a rede não suporta compartilhamento.
       */
      const isFirstScreen =
        source.purpose === 'screen' &&
        ordered.findIndex((s) => s.purpose === 'screen') ===
          ordered.indexOf(source);

      if (isFirstScreen) {
        const minimum = table[table.length - 1]!;
        profiles.set(source.sourceId, minimum);
        remaining -= minimum.maxKbps * peers;
      } else {
        paused.push(source.sourceId);
      }
    }

    const projectedKbps =
      audioKbps +
      [...profiles.entries()].reduce(
        (total, [, profile]) => total + profile.maxKbps * peers,
        0,
      );

    return {
      budgetKbps,
      projectedKbps,
      audioKbps,
      profiles,
      paused,
      notice: this.buildNotice({
        budgetKbps,
        projectedKbps,
        peers,
        videoCount: videoSources.length,
        paused,
        profiles,
        videoSources,
      }),
      critical: projectedKbps > budgetKbps,
    };
  }

  /**
   * Mensagem para a interface, COM OS NÚMEROS.
   *
   * "Qualidade reduzida" não ajuda ninguém a agir. "2 telas × 7 pares pedem 35
   * Mbps e seu upload é 10 Mbps" diz exatamente o que fazer: fechar uma tela ou
   * chamar menos gente. É o que transforma um limite em informação.
   */
  private buildNotice(input: {
    budgetKbps: number;
    projectedKbps: number;
    peers: number;
    videoCount: number;
    paused: string[];
    profiles: Map<string, VideoProfile>;
    videoSources: LocalTrack[];
  }): string | null {
    const mbps = (kbps: number) => (kbps / 1000).toFixed(1);

    if (input.paused.length > 0) {
      const labels = input.paused
        .map((id) => input.videoSources.find((s) => s.sourceId === id)?.label ?? id)
        .join(', ');
      return (
        `${input.videoCount} fontes de vídeo × ${input.peers} ${
          input.peers === 1 ? 'par' : 'pares'
        } excedem seu upload de ${mbps(input.budgetKbps)} Mbps. ` +
        `Pausado: ${labels}. Feche uma fonte ou aumente o limite nas configurações.`
      );
    }

    if (input.projectedKbps > input.budgetKbps) {
      return (
        `Upload projetado de ${mbps(input.projectedKbps)} Mbps contra ` +
        `${mbps(input.budgetKbps)} Mbps disponíveis. A qualidade está no mínimo — ` +
        `nesta rede, compartilhar tela para ${input.peers} ` +
        `${input.peers === 1 ? 'pessoa' : 'pessoas'} não é viável.`
      );
    }

    // Aviso PREVENTIVO acima de 75%: o usuário tem chance de fechar algo antes
    // de a chamada degradar, em vez de descobrir depois.
    const usage = input.projectedKbps / input.budgetKbps;
    if (usage > 0.75 && input.videoCount > 0) {
      const worst = [...input.profiles.values()].some((p) => p.scaleDownBy > 1);
      return (
        `Usando ${Math.round(usage * 100)}% do orçamento de upload ` +
        `(${mbps(input.projectedKbps)} de ${mbps(input.budgetKbps)} Mbps).` +
        (worst ? ' A resolução foi reduzida para caber.' : '')
      );
    }

    return null;
  }

  /**
   * Aplica a decisão nos senders de todos os pares.
   *
   * `setParameters` é assíncrono e pode falhar se a negociação estiver em
   * andamento. A falha é registrada e ignorada: a próxima volta do laço
   * reaplica, e insistir aqui competiria com a renegociação.
   */
  async apply(peers: PeerState[], decision: AllocationDecision): Promise<void> {
    for (const peer of peers) {
      for (const [sourceId, sender] of peer.senders) {
        const profile = decision.profiles.get(sourceId);
        const isPaused = decision.paused.includes(sourceId);

        if (isPaused) {
          if (!this.pausedSources.has(sourceId)) {
            this.pausedSources.add(sourceId);
            this.options.onSourcePaused?.(
              sourceId,
              true,
              decision.notice ?? 'orçamento de upload excedido',
            );
          }
          /*
           * Pausa desabilitando o encoding, não removendo o track.
           *
           * Remover exigiria renegociar com todos os pares para pausar, e de
           * novo para retomar — dois round-trips de SDP por par por oscilação
           * de banda. `active: false` para de enviar imediatamente e retoma na
           * mesma hora, sem tocar no SDP.
           */
          await this.setEncoding(sender, { active: false });
          continue;
        }

        if (this.pausedSources.delete(sourceId)) {
          this.options.onSourcePaused?.(sourceId, false, 'orçamento recuperado');
        }

        if (!profile) continue;

        const previous = this.current.get(sourceId);
        if (
          previous &&
          previous.maxKbps === profile.maxKbps &&
          previous.maxFramerate === profile.maxFramerate &&
          previous.scaleDownBy === profile.scaleDownBy
        ) {
          // Nada mudou: `setParameters` desnecessário custa uma renegociação
          // interna do codificador em alguns navegadores.
          continue;
        }

        await this.setEncoding(sender, {
          active: true,
          maxBitrate: profile.maxKbps * 1000,
          maxFramerate: profile.maxFramerate,
          scaleResolutionDownBy: profile.scaleDownBy,
        });
        this.current.set(sourceId, profile);
      }
    }

    this.options.onDecision?.(decision);
  }

  private async setEncoding(
    sender: RTCRtpSender,
    encoding: RTCRtpEncodingParameters,
  ): Promise<void> {
    try {
      const parameters = sender.getParameters();
      /*
       * `encodings` pode vir VAZIO antes da primeira negociação.
       *
       * Escrever `parameters.encodings[0] = ...` num array vazio não cria o
       * elemento — a atribuição funciona, mas o navegador ignora um encoding
       * que não existia no momento da negociação. Criar o objeto quando falta é
       * o que faz a primeira aplicação valer.
       */
      if (parameters.encodings.length === 0) {
        parameters.encodings = [encoding];
      } else {
        parameters.encodings[0] = { ...parameters.encodings[0], ...encoding };
      }
      await sender.setParameters(parameters);
    } catch (error) {
      // Falha comum durante renegociação. A próxima volta do laço reaplica.
      console.debug('[banda] setParameters recusado, será reaplicado', error);
    }
  }

  /**
   * Mede a banda disponível a partir das estatísticas dos pares.
   *
   * Usa o MENOR `availableOutgoingBitrate` entre os pares, e não a média. O
   * gargalo é o upload local, compartilhado por todos os fluxos — se um par
   * reporta menos, é porque a fila está enchendo, e a média esconderia isso.
   */
  async measure(peers: PeerState[]): Promise<number | null> {
    let lowest: number | null = null;

    for (const peer of peers) {
      let report: RTCStatsReport;
      try {
        report = await peer.connection.getStats();
      } catch {
        continue;
      }
      for (const entry of report.values()) {
        if (
          entry.type === 'candidate-pair' &&
          entry.nominated === true &&
          typeof entry.availableOutgoingBitrate === 'number'
        ) {
          const kbps = Math.round(entry.availableOutgoingBitrate / 1000);
          if (lowest === null || kbps < lowest) lowest = kbps;
        }
      }
    }

    if (lowest !== null) {
      this.budget = { ...this.budget, estimatedKbps: lowest };
    }
    return lowest;
  }

  /**
   * Inicia o laço de ajuste.
   *
   * 2 segundos: rápido o suficiente para reagir a uma queda antes de o áudio
   * cortar, lento o suficiente para não competir com o algoritmo de
   * congestionamento do próprio WebRTC — que trabalha na escala de centenas de
   * milissegundos. Um laço mais rápido lutaria contra ele.
   */
  start(getState: () => { peers: PeerState[]; localTracks: LocalTrack[] }): void {
    this.stop();
    this.timer = setInterval(() => {
      void (async () => {
        const state = getState();
        if (state.peers.length === 0) return;

        await this.measure(state.peers);
        /*
         * O contador sobe com vídeo saindo e ZERA sem ele.
         *
         * Zerar é tão importante quanto contar: quem para de compartilhar volta
         * ao estado frio, e a estimativa colhida durante o compartilhamento não
         * deve continuar limitando a PRÓXIMA tela. Ela mediu um link que estava
         * carregado; o link agora está livre.
         *
         * A pausa NÃO está na faixa — está nos `encodings` do sender.
         *
         * `LocalTrack` não tem campo `active`; o governador pausa escrevendo
         * `active: false` nos parâmetros do `RTCRtpSender`, de propósito (remover
         * a faixa exigiria dois round-trips de SDP por par a cada oscilação). Só
         * `pausedSources` sabe quem está pausado.
         *
         * Contar uma fonte pausada manteria a estimativa "quente" com nada
         * saindo — exatamente o estado que produz o travamento que este
         * mecanismo existe para evitar.
         *
         * `readyState` entra porque uma tela cujo compartilhamento foi encerrado
         * pelo botão do NAVEGADOR fica na lista com a faixa em `ended`. Ela não
         * envia mais nada, e contá-la é o mesmo erro.
         */
        const enviandoVideo = state.localTracks.some(
          (track) =>
            track.purpose !== 'microphone' &&
            !this.pausedSources.has(track.sourceId) &&
            track.track.readyState === 'live' &&
            track.track.enabled,
        );
        this.videoActiveSamples = enviandoVideo ? this.videoActiveSamples + 1 : 0;

        const decision = this.decide({
          peerCount: state.peers.length,
          localTracks: state.localTracks,
        });

        /*
         * Histerese nos DOIS sentidos.
         *
         * Subir de qualidade cedo demais é tão ruim quanto descer tarde: a
         * banda volta, o governador sobe o perfil, a fila enche de novo e ele
         * desce — oscilação visível como resolução pulsando. Exigir amostras
         * consecutivas em cada direção estabiliza.
         */
        if (decision.projectedKbps > decision.budgetKbps) {
          this.overBudgetStreak += 1;
          this.underBudgetStreak = 0;
        } else {
          this.underBudgetStreak += 1;
          this.overBudgetStreak = 0;
        }

        const shouldAct =
          this.overBudgetStreak >= HYSTERESIS_SAMPLES ||
          this.underBudgetStreak >= HYSTERESIS_SAMPLES;

        if (shouldAct) {
          await this.apply(state.peers, decision);
        }
      })();
    }, 2000);
  }

  stop(): void {
    if (this.timer !== undefined) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  /**
   * Responde "posso ligar mais uma tela?" ANTES de ligar.
   *
   * É o que implementa a regra de produto: acima de 4 pares, avisa antes de
   * permitir a segunda tela. A resposta traz o motivo com números, para o aviso
   * não ser um "tem certeza?" vazio.
   */
  canAddScreen(input: {
    peerCount: number;
    localTracks: LocalTrack[];
  }): { allowed: boolean; warning: string | null } {
    const peers = Math.max(1, input.peerCount);
    const screens = input.localTracks.filter((t) => t.purpose === 'screen').length;

    const hypothetical: LocalTrack[] = [
      ...input.localTracks,
      {
        track: null as unknown as MediaStreamTrack,
        purpose: 'screen',
        sourceId: '__hipotetica__',
        label: 'nova tela',
      },
    ];

    const decision = this.decide({ peerCount: peers, localTracks: hypothetical });
    const mbps = (kbps: number) => (kbps / 1000).toFixed(1);

    if (decision.paused.length > 0 || decision.critical) {
      return {
        allowed: false,
        warning:
          `Não cabe: ${screens + 1} telas para ${peers} ` +
          `${peers === 1 ? 'par' : 'pares'} pedem ${mbps(decision.projectedKbps)} Mbps ` +
          `e o orçamento é ${mbps(decision.budgetKbps)} Mbps.`,
      };
    }

    if (screens >= 1 && peers > 4) {
      return {
        allowed: true,
        warning:
          `Com ${peers} pares, a segunda tela vai custar ` +
          `${mbps(decision.projectedKbps - decision.audioKbps)} Mbps de upload. ` +
          `Cabe no seu orçamento de ${mbps(decision.budgetKbps)} Mbps, mas a ` +
          `qualidade das duas telas vai cair.`,
      };
    }

    return { allowed: true, warning: null };
  }
}
